const MCC_FLYOUT_PER_PAGE = 4;


const initMccFlyoutCarousel = (panel) => {
  if (panel.dataset.mccCarouselBound === 'true') return;
  panel.dataset.mccCarouselBound = 'true';

  const viewport = panel.querySelector('.mcc-flyout__carousel-viewport');
  const columns  = panel.querySelector('.mcc-flyout__columns');
  const controls = panel.querySelector('.mcc-flyout__carousel-controls');
  const prevBtn  = panel.querySelector('.mcc-flyout__carousel-btn--prev');
  const nextBtn  = panel.querySelector('.mcc-flyout__carousel-btn--next');
  const colEls   = columns ? Array.from(columns.children) : [];

  if (!viewport || !columns || !controls || colEls.length <= MCC_FLYOUT_PER_PAGE) return;

  panel.classList.add('is-carousel');
  controls.hidden = false;
  prevBtn.hidden = true;
  nextBtn.hidden = false;
  let page = 0;
  const totalPages = Math.ceil(colEls.length / MCC_FLYOUT_PER_PAGE);

  const render = () => {
    if (!viewport.offsetWidth) return;
    const gap = parseFloat(getComputedStyle(columns).columnGap) || 20;
    const colWidth = (viewport.offsetWidth - (MCC_FLYOUT_PER_PAGE - 1) * gap) / MCC_FLYOUT_PER_PAGE;
    columns.style.transform = `translateX(-${page * MCC_FLYOUT_PER_PAGE * (colWidth + gap)}px)`;
    prevBtn.hidden = page === 0;
    nextBtn.hidden = page >= totalPages - 1;
  };

  prevBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (page > 0) { viewport.focus({ preventScroll: true }); page--; render(); }
  });
  nextBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (page < totalPages - 1) { viewport.focus({ preventScroll: true }); page++; render(); }
  });

  panel._mccFlyoutRender = render;

  new ResizeObserver(render).observe(viewport);
};

if (!customElements.get('superstore-command-center')) {
  class SuperstoreCommandCenter extends HTMLElement {
    constructor() {
      super();
      this.handleKeydown = this.handleKeydown.bind(this);
      this.activePanelId = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.flyoutHost = this.querySelector('.mcc-main');
      this.flyout = this.querySelector('[data-mcc-flyout]');
      this.triggers = Array.from(this.querySelectorAll('[data-mcc-rail-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-mcc-flyout-panel]'));
      this.closeButtons = Array.from(this.querySelectorAll('[data-mcc-flyout-close]'));

      if (this.triggers.length) {
        this.setActivePanel(this.triggers[0].dataset.mccPanelId);
      }

      this.triggers.forEach((trigger) => {
        trigger.addEventListener(
          'click',
          () => {
            const panelId = trigger.dataset.mccPanelId;
            if (this.activePanelId === panelId && this.flyout && !this.flyout.hidden) {
              this.closeFlyout();
              return;
            }
            this.openFlyout(panelId);
          },
          { signal }
        );
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeFlyout(), { signal });
      });

      this.flyout?.addEventListener(
        'click',
        (event) => {
          if (event.target === this.flyout) this.closeFlyout();
        },
        { signal }
      );

      this.addEventListener('keydown', this.handleKeydown, { signal });

      // In the theme editor, when a block setting changes the section re-renders
      // and Shopify auto-re-selects the edited block by firing shopify:block:select
      // with detail.load = true. On mobile preview the 3 slots stack vertically,
      // putting slot 3 near the footer — so scrollIntoView sends the page to the
      // bottom. Save the scroll position before the re-render and restore it when
      // the auto-reselect fires (but not when the user deliberately clicks a block).
      if (window.Shopify && window.Shopify.designMode) {
        const self = this;
        let savedScrollY = null;
        let cleanupTimer = null;

        document.addEventListener('shopify:section:load', function (e) {
          const sectionEl = document.getElementById('shopify-section-' + e.detail.sectionId);
          if (!sectionEl || !sectionEl.contains(self)) return;
          savedScrollY = window.scrollY;
          clearTimeout(cleanupTimer);
          cleanupTimer = setTimeout(function () { savedScrollY = null; }, 400);
        }, { signal });

        document.addEventListener('shopify:block:select', function (e) {
          if (e.detail && e.detail.load && savedScrollY !== null && self.contains(e.target)) {
            const y = savedScrollY;
            savedScrollY = null;
            clearTimeout(cleanupTimer);
            setTimeout(function () { window.scrollTo(0, y); }, 0);
          } else {
            savedScrollY = null;
            clearTimeout(cleanupTimer);
          }
        }, { signal });
      }

      this.addEventListener(
        'click',
        (event) => {
          const atcButton = event.target.closest('[data-mcc-bundle-atc]');
          if (atcButton) {
            event.preventDefault();
            this.handleBundleAtc(atcButton);
          }
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.initialized = false;
      this.abortController = null;
    }

    handleKeydown(event) {
      if (event.key === 'Escape') this.closeFlyout();
    }

    setTriggerState(panelId) {
      this.triggers.forEach((trigger) => {
        const isActive =
          panelId &&
          trigger.dataset.mccPanelId === panelId &&
          this.flyoutHost?.classList.contains('is-flyout-open');
        trigger.classList.toggle('is-active', Boolean(isActive));
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    setActivePanel(panelId) {
      this.panels.forEach((panel) => {
        const isActive = panel.id === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;

        if (isActive) {
          if (!panel.dataset.mccCarouselBound) initMccFlyoutCarousel(panel);
          if (panel._mccFlyoutRender) window.requestAnimationFrame(panel._mccFlyoutRender);
        }
      });
    }

    closeFlyout() {
      if (!this.flyout || !this.flyoutHost) return;
      this.activePanelId = null;
      this.flyoutHost.classList.remove('is-flyout-open');
      this.flyout.hidden = true;
      this.setTriggerState(null);
      if (this._flyoutOpener?.isConnected) this._flyoutOpener.focus({ preventScroll: true });
      this._flyoutOpener = null;
    }

    openFlyout(panelId) {
      if (!this.flyout || !this.flyoutHost || !panelId) return;
      const hasMatch = this.panels.some((panel) => panel.id === panelId);
      if (!hasMatch) {
        this.closeFlyout();
        return;
      }
      this._flyoutOpener = document.activeElement;
      this.setActivePanel(panelId);
      this.activePanelId = panelId;
      this.flyoutHost.classList.add('is-flyout-open');
      this.flyout.hidden = false;
      this.setTriggerState(panelId);
      window.requestAnimationFrame(() => {
        const panel = this.panels.find((p) => p.id === panelId);
        const first = panel?.querySelector('a, button, [tabindex="0"]');
        if (first) first.focus({ preventScroll: true });
      });
    }

    async handleBundleAtc(button) {
      const variantIds = (button.dataset.mccBundleAtc || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (!variantIds.length) return;

      const originalHTML = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Adding…';

      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: variantIds.map((id) => ({ id: Number(id), quantity: 1 }))
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Unable to add items to cart.');
        }

        // /cart/add.js's own response can't be trusted for cart totals/sections
        // right after a multi-item add — same issue documented in
        // component-atc-button.js. Fetch fresh cart state and section markup
        // separately instead of using this response's own data.
        const [cartData, sectionsData] = await Promise.all([
          fetch(`${root}cart.js`).then((r) => r.json()),
          fetch(`${root}?sections=cart-drawer`).then((r) => r.json())
        ]);

        const sectionHtml = sectionsData?.['cart-drawer'];
        if (sectionHtml) {
          const wrapper = document.getElementById('shopify-section-cart-drawer');
          if (wrapper) {
            wrapper.outerHTML = sectionHtml;
          } else {
            const newDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const newCart = newDoc.getElementById('laki-cart');
            const curCart = document.getElementById('laki-cart');
            if (newCart && curCart) curCart.innerHTML = newCart.innerHTML;
          }
        }

        if (typeof cartData?.item_count === 'number') {
          document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cartData.item_count;
          });
        }

        document.querySelector('laki-cart')?.open();

        window.LakiEvents?.emit?.(window.LakiEvents.EVENTS.CART_ADD, {
          count: cartData?.item_count,
          items: cartData?.items
        });

        window.LakiCoreCharge?.sync();
      } catch (error) {
        window.alert(error.message);
      } finally {
        button.disabled = false;
        button.innerHTML = originalHTML;
      }
    }
  }

  customElements.define('superstore-command-center', SuperstoreCommandCenter);
}
